import {getSizedImageUrl} from '@shopify/theme-images';

import NativeScrollbar from './native-scrollbar';

const selectors = {
  siblingsInnerHolder: '[data-sibling-inner]',
  siblingCount: '[data-sibling-count]',
  siblingHolder: '[data-sibling-holder]',
  siblingFieldset: '[data-sibling-fieldset]',
  siblingLink: '[data-sibling-link]',
  siblingLinkValue: 'data-sibling-link',
  siblingIndexValue: 'data-sibling-index',
  siblingImageValue: 'data-sibling-image',
  productInfo: '[data-product-information]',
  siblingAddedImage: 'data-sibling-added-image',
  dataSiblingDefaultImage: 'data-sibling-default-image',
  link: '[data-grid-link]',
  linkValue: 'data-grid-link',
  productGridItem: '[data-product-grid-item]',
  slide: '[data-grid-slide]',
  targetHolder: '[data-sibling-target-holder]',
  targetElement: 'data-sibling-target-element',
};

const classes = {
  visible: 'is-visible',
  fade: 'is-fade',
  stopEvents: 'no-events',
  active: 'is-active',
};

class SiblingSwatch {
  constructor(swatch, product) {
    this.swatch = swatch;
    this.product = product;
    this.swatchLinkValue = this.swatch.hasAttribute(selectors.siblingLinkValue) ? this.swatch.getAttribute(selectors.siblingLinkValue) : '';
    this.imageSrcValue = this.swatch.hasAttribute(selectors.siblingImageValue) ? this.swatch.getAttribute(selectors.siblingImageValue) : '';
    this.swatchIndex = this.swatch.hasAttribute(selectors.siblingIndexValue) ? this.swatch.getAttribute(selectors.siblingIndexValue) : '';
    this.productLinks = this.product.querySelectorAll(selectors.link);
    this.targetHolder = this.product.querySelectorAll(selectors.targetHolder);
    this.slideImage = this.product.querySelector(selectors.slide);
    this.linkDetailsExist = this.productLinks.length && this.swatchLinkValue !== '';
    this.imageDetailsExist = this.slideImage && this.imageSrcValue !== '';
    this.targetDetailsExist = this.targetHolder.length && this.swatchIndex !== '';
    this.newLink = null;
    this.imageSrc = null;
    this.loadedImage = null;
    this.preloaderImg = null;

    this.init();
  }

  init() {
    if (this.linkDetailsExist || this.imageDetailsExist || this.targetDetailsExist) {
      this.swatch.addEventListener('mouseenter', () => this.changeDetails());
    }

    if (this.linkDetailsExist || this.imageDetailsExist) {
      this.product.addEventListener('mouseleave', () => {
        if (this.imageDetailsExist && this.slideImage.hasAttribute(selectors.siblingAddedImage) && this.slideImage.getAttribute(selectors.siblingAddedImage) !== '') {
          this.product.classList.remove(classes.active);
          this.fadeImage(true);
        }

        if (this.linkDetailsExist) {
          this.newLink = this.productLinks[0].hasAttribute(selectors.linkValue) ? this.productLinks[0].getAttribute(selectors.linkValue) : this.swatchLinkValue;
          this.changeLink();
        }
      });
    }

    if (this.productLinks.length) {
      this.swatch.addEventListener('click', () => {
        this.productLinks[0].click();
      });
    }
  }

  changeDetails() {
    this.product.classList.add(classes.active);

    if (this.linkDetailsExist) {
      this.newLink = this.swatchLinkValue;
      this.changeLink();
    }

    if (this.imageDetailsExist) {
      this.changeImage();
    }

    if (this.targetDetailsExist) {
      this.changeText();
    }
  }

  changeLink() {
    // Add current sibling link to PGI link
    this.productLinks.forEach((productLink) => {
      productLink.href = this.newLink;
    });
  }

  changeText() {
    this.targetHolder.forEach((targetHolder) => {
      const targetElement = targetHolder.querySelector(`[${selectors.targetElement}="${this.swatchIndex}"]`);
      const activeTarget = targetHolder.querySelector(`[${selectors.targetElement}].${classes.active}`);

      if (activeTarget) {
        activeTarget.classList.remove(classes.active);
      }

      if (targetElement) {
        targetElement.classList.add(classes.active);
      }
    });
  }

  changeImage() {
    // Add current sibling swatch image to PGI image
    if (!this.loadedImage) {
      const ratio = window.devicePixelRatio || 1;
      const pixels = this.slideImage.offsetWidth * ratio;
      const widthRounded = Math.ceil(pixels / 180) * 180;
      this.imageSrc = getSizedImageUrl(this.imageSrcValue, `${widthRounded}x`);
      this.preloaderImg = document.createElement('img');
      this.preloaderImg.src = this.imageSrc;

      this.preloaderImg.addEventListener('load', () => {
        this.loadedImage = this.imageSrc;

        this.fadeImage();
      });
    } else if (this.loadedImage && this.slideImage.hasAttribute(selectors.siblingAddedImage) && this.slideImage.getAttribute(selectors.siblingAddedImage) !== this.loadedImage) {
      this.fadeImage();
    }
  }

  fadeImage(defaultImage = false) {
    // Add fade effect when the sibling product images are added
    this.slideImage.classList.add(classes.fade);

    if (!this.slideImage.hasAttribute(selectors.dataSiblingDefaultImage)) {
      this.slideImage.setAttribute(selectors.dataSiblingDefaultImage, this.slideImage.style.backgroundImage);
    }

    const animationDuration = window.getComputedStyle(this.slideImage).getPropertyValue('animation-duration');
    const timeoutDelay = animationDuration ? parseFloat(animationDuration) * 1000 : 0;
    const imageUrl = defaultImage && this.slideImage.hasAttribute(selectors.dataSiblingDefaultImage) ? this.slideImage.getAttribute(selectors.dataSiblingDefaultImage) : `url("${this.loadedImage}")`;
    const imageAttr = defaultImage && this.slideImage.hasAttribute(selectors.dataSiblingDefaultImage) ? '' : this.loadedImage;

    // Change image in the middle of fade effect
    setTimeout(() => {
      this.slideImage.style.setProperty('background-image', imageUrl);
      this.slideImage.setAttribute(selectors.siblingAddedImage, imageAttr);
    }, timeoutDelay / 2);

    setTimeout(() => {
      this.slideImage.classList.remove(classes.fade);
    }, timeoutDelay);

    this.preloaderImg = null;
  }
}

class Sibling {
  constructor(holder, product) {
    this.holder = holder;
    this.product = product;
    this.siblingScrollbar = this.holder.querySelector(selectors.siblingsInnerHolder);
    this.siblingCount = this.holder.querySelector(selectors.siblingCount);
    this.siblingFieldset = this.holder.querySelector(selectors.siblingFieldset);
    this.siblingLinks = this.holder.querySelectorAll(selectors.siblingLink);
    this.productInfo = this.holder.closest(selectors.productInfo);
    this.productLink = this.holder.closest(selectors.link);
    this.hideSwatchesTimer = 0;

    this.init();
  }

  init() {
    this.initScrollbar();

    if (this.siblingCount && this.siblingFieldset && this.productInfo) {
      this.siblingCount.addEventListener('mouseenter', () => this.showSiblings());

      // Prevent color swatches blinking on mouse move
      this.productInfo.addEventListener('mouseleave', () => this.hideSiblings());
    }

    if (this.siblingLinks.length) {
      this.siblingLinks.forEach((siblingLink) => {
        new SiblingSwatch(siblingLink, this.product);
      });
    }
  }

  showSiblings() {
    if (this.hideSwatchesTimer) clearTimeout(this.hideSwatchesTimer);

    if (this.productLink) {
      this.productLink.classList.add(classes.stopEvents);
    }

    this.siblingFieldset.classList.add(classes.visible);
  }

  hideSiblings() {
    this.hideSwatchesTimer = setTimeout(() => {
      if (this.productLink) {
        this.productLink.classList.remove(classes.stopEvents);
      }

      this.siblingFieldset.classList.remove(classes.visible);
    }, 100);
  }

  initScrollbar() {
    if (this.siblingScrollbar) {
      new NativeScrollbar(this.siblingScrollbar);
    }
  }
}

class Siblings {
  constructor(section) {
    this.container = section.container;
    this.siblingHolders = this.container.querySelectorAll(`${selectors.productGridItem} ${selectors.siblingHolder}`);

    if (this.siblingHolders.length) {
      this.siblingHolders.forEach((siblingHolder) => {
        new Sibling(siblingHolder, siblingHolder.closest(selectors.productGridItem));
      });
    }
  }
}

const siblings = {
  onLoad() {
    new Siblings(this);
  },
};

export {siblings, Siblings};
